// ============================================================================
// Mon Temps d'Étude — Banque fin CM1 (entrée CM2)
// Production conforme spec v2.1 ratifiée par Ana le 19/09/2026
// Worktree: feature/lumen-diag-bank-2026-09
// Auteur: Lumen MTE <lumen@mte.local>
// Date production: 2026-09-19
// ============================================================================
//
// PATTERN UNIVERSEL: target = CM2 → diagnostique les acquis de fin de CM1.
// Banque resegmentation de l'ancien `banque-questions-canon.ts`.
//
// SOURCES OFFICIELLES:
//   - CM1 fluence: ≥ 110 mots/min (BO 17/04/2025 cycle 3)
//   - CM1 numération: nombres décimaux (jusqu'à 0,001) + grands nombres
// ============================================================================

import type { BanqueItem } from '../lib/banque-types';

type CM1Item = BanqueItem;  // schoolLevel: 'CM1' natif

const ITEMS_LECTURE_CODE: CM1Item[] = [
  {
    id: 'FR-CM1-LEC-01',
    schoolLevel: 'CM1',
    archetype: 'saisie',
    competence: 'FR-CM1-LEC-01',
    enonce: "Combien de syllabes a le mot 'inconséquemment' ? (réponse en chiffre)",
    bonneReponse: '6',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO 17/04/2025 · cycle 3 · CM1 · Lecture · mots longs',
      preuve_url: null,
      instruction_audio: { cm1: 'absent' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CM1-LEC-02',
    schoolLevel: 'CM1',
    archetype: 'qcm',
    competence: 'FR-CM1-LEC-02',
    enonce: "Quel mot contient une voyelle nasale ?",
    options: ['papa', 'enfant', 'chat', 'rose'],
    bonneReponse: 'enfant',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO 17/04/2025 · cycle 3 · CM1 · Lecture · phonétique',
      preuve_url: null,
      instruction_audio: { cm1: 'absent' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CM1-LEC-03',
    schoolLevel: 'CM1',
    archetype: 'qcm',
    competence: 'FR-CM1-LEC-03',
    enonce: "Comment prononce-t-on 'ill' dans 'fille' ?",
    options: ['[ij]', '[i]', '[j]', '[il]'],
    bonneReponse: '[ij]',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO 17/04/2025 · cycle 3 · CM1 · Lecture · phonétique avancée',
      preuve_url: null,
      instruction_audio: { cm1: 'absent' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CM1-LEC-04',
    schoolLevel: 'CM1',
    archetype: 'qcm',
    competence: 'FR-CM1-LEC-04',
    enonce: "Quel mot contient un son [ε] (è ouvert) ?",
    options: ['père', 'papa', 'pied', 'cou'],
    bonneReponse: 'père',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO 17/04/2025 · cycle 3 · CM1 · Lecture · oppositions phonétiques',
      preuve_url: null,
      instruction_audio: { cm1: 'absent' },
      stimulus_audio: { required: false },
    },
  },
];

const ITEMS_FLUENCE: CM1Item[] = [
  {
    id: 'FR-CM1-FLU-01',
    schoolLevel: 'CM1',
    archetype: 'lecture-voix-haute',
    competence: 'FR-CM1-FLU-01',
    enonce: 'Lis ce texte à voix haute pendant 1 minute.',
    textALire: "Le roi Arthur régnait sur la Bretagne. Il possédait une épée magique, Excalibur. Sa femme, la reine Guenièvre, demeurait au château de Camelot. Les chevaliers de la Table Ronde se réunissaient pour de grandes aventures. Merlin l'enchanteur guidait le roi par ses pouvoirs mystérieux.",
    dureeMaxSecondes: 60,
    evalMode: 'HUMAN',
    meta: {
      texte_usage: 'entrainement_fluence',
      titre: 'La légende du roi Arthur',
      mots_total: 56,
      source: 'domaine_public',
      auteur_licence: 'Légendes arthuriennes · domaine public',
      niveau_progression: 'reference',
      source_officielle: 'BO 17/04/2025 · cycle 3 · CM1 · Lecture · fluence · cible 110 mots/min',
      preuve_url: null,
      instruction_audio: { cm1: 'absent' },
      stimulus_audio: { required: false },
      seuil_officiel: 110,
    },
  },
  {
    id: 'FR-CM1-FLU-02',
    schoolLevel: 'CM1',
    archetype: 'lecture-voix-haute',
    competence: 'FR-CM1-FLU-02',
    enonce: 'Lis ce texte à voix haute pendant 1 minute.',
    textALire: "En 1492, Christophe Colomb partit de l'Espagne avec trois navires : la Pinta, la Niña et la Santa María. Après plusieurs semaines de navigation, son équipage aperçut enfin la terre. Ils pensaient avoir atteint l'Asie mais ils découvraient en réalité l'Amérique. Ce voyage marqua le début d'une nouvelle ère d'échanges entre les continents.",
    dureeMaxSecondes: 60,
    evalMode: 'HUMAN',
    meta: {
      texte_usage: 'evaluation_1_minute',
      titre: 'Le voyage de Christophe Colomb',
      mots_total: 70,
      source: 'mte_propre',
      auteur_licence: 'MTE — Equipe pédagogique 2026',
      niveau_progression: 'reference',
      source_officielle: 'BO 17/04/2025 · cycle 3 · CM1 · Lecture · fluence · cible 110 mots/min',
      preuve_url: null,
      instruction_audio: { cm1: 'absent' },
      stimulus_audio: { required: false },
      seuil_officiel: 110,
    },
  },
  {
    id: 'FR-CM1-FLU-03',
    schoolLevel: 'CM1',
    archetype: 'lecture-voix-haute',
    competence: 'FR-CM1-FLU-03',
    enonce: 'Lis ce texte à voix haute pendant 1 minute.',
    textALire: "Les pyramides d'Égypte fascinent les chercheurs depuis des siècles. Ces constructions monumentales ont été érigées il y a plus de quatre mille ans. Les ouvriers étaient extrêmement bien organisés et employaient des techniques de construction révolutionnaires. Aujourd'hui encore, on s'interroge sur les méthodes exactes qu'ils utilisaient pour hisser d'énormes blocs de pierre jusqu'au sommet. Le mystère reste entier pour notre époque moderne.",
    dureeMaxSecondes: 60,
    evalMode: 'HUMAN',
    meta: {
      texte_usage: 'evaluation_1_minute',
      titre: 'Le mystère des pyramides',
      mots_total: 91,
      source: 'mte_propre',
      auteur_licence: 'MTE — Equipe pédagogique 2026',
      niveau_progression: 'reference',
      source_officielle: 'BO 17/04/2025 · cycle 3 · CM1 · Lecture · fluence · cible 110 mots/min · evaluation formelle',
      preuve_url: null,
      instruction_audio: { cm1: 'absent' },
      stimulus_audio: { required: false },
      seuil_officiel: 110,
    },
  },
];

const ITEMS_COMPREHENSION: CM1Item[] = [
  {
    id: 'FR-CM1-COMP-01',
    schoolLevel: 'CM1',
    archetype: 'qcm',
    competence: 'FR-CM1-COMP-01',
    enonce: "Lis : 'Alice prend son parapluie. Le ciel est couvert de nuages noirs.' Pourquoi ?",
    options: ['Pour faire joli', "Parce qu'il va pleuvoir", 'Pour protéger du soleil', 'Pour se cacher'],
    bonneReponse: "Parce qu'il va pleuvoir",
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO 17/04/2025 · cycle 3 · CM1 · Compréhension · inférence simple',
      preuve_url: null,
      instruction_audio: { cm1: 'absent' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CM1-COMP-02',
    schoolLevel: 'CM1',
    archetype: 'qcm',
    competence: 'FR-CM1-COMP-02',
    enonce: "Lis : 'Marc n'arrête pas de regarder la montre. Il soupire. Sa mère lui demande ce qu'il a.' Que ressent Marc ?",
    options: ['De la joie', "De l'ennui", "De l'impatience", 'De la colère'],
    bonneReponse: "De l'impatience",
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO 17/04/2025 · cycle 3 · CM1 · Compréhension · inférence émotion implicite',
      preuve_url: null,
      instruction_audio: { cm1: 'absent' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CM1-COMP-03',
    schoolLevel: 'CM1',
    archetype: 'qcm',
    competence: 'FR-CM1-COMP-03',
    enonce: "Lis : 'Ce roman raconte les aventures d'un détective privé. L'auteur utilise un style humoristique. La chute est inattendue.' Quel type ?",
    options: ['Un documentaire', 'Un roman policier humoristique', 'Un poème', 'Un manuel'],
    bonneReponse: 'Un roman policier humoristique',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO 17/04/2025 · cycle 3 · CM1 · Compréhension · identifier genre',
      preuve_url: null,
      instruction_audio: { cm1: 'absent' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CM1-COMP-04',
    schoolLevel: 'CM1',
    archetype: 'qcm',
    competence: 'FR-CM1-COMP-04',
    enonce: "Lis : 'Le narrateur décrit un château sombre. Les fenêtres sont brisées. Un chien hurle au loin.' Quelle ambiance ?",
    options: ['Joyeuse et légère', 'Mystérieuse et inquiétante', 'Tranquille et sereine', 'Sportive et énergique'],
    bonneReponse: 'Mystérieuse et inquiétante',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO 17/04/2025 · cycle 3 · CM1 · Compréhension · identifier ambiance',
      preuve_url: null,
      instruction_audio: { cm1: 'absent' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CM1-COMP-05',
    schoolLevel: 'CM1',
    archetype: 'saisie',
    competence: 'FR-CM1-COMP-05',
    enonce: "Lis : 'Dans les contes de fées, le héros doit souvent triompher d'obstacles avant de trouver le bonheur. C'est un schéma classique du genre.' Quel est ce schéma narratif ?",
    consigne: 'Réponds en un seul mot (le nom du schéma narratif).',
    bonneReponse: 'schéma narratif',
    tolerance: 2,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO 17/04/2025 · cycle 3 · CM1 · Compréhension · identifier schéma',
      preuve_url: null,
      instruction_audio: { cm1: 'absent' },
      stimulus_audio: { required: false },
    },
  },
];

const ITEMS_VOCABULAIRE: CM1Item[] = [
  {
    id: 'FR-CM1-VOC-01',
    schoolLevel: 'CM1',
    archetype: 'qcm',
    competence: 'FR-CM1-VOC-01',
    enonce: "Quel est le sens de 'voler' dans 'Cet oiseau peut voler' ?",
    options: ['Dérober', "Se déplacer dans les airs", 'Faire un voyage', 'Tomber'],
    bonneReponse: "Se déplacer dans les airs",
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO 17/04/2025 · cycle 3 · CM1 · Vocabulaire · polysémie',
      preuve_url: null,
      instruction_audio: { cm1: 'absent' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CM1-VOC-02',
    schoolLevel: 'CM1',
    archetype: 'qcm',
    competence: 'FR-CM1-VOC-02',
    enonce: "Quel mot a un sens figuré dans 'Le temps passe trop vite' ?",
    options: ['temps', 'passe', 'trop', 'vite'],
    bonneReponse: 'passe',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO 17/04/2025 · cycle 3 · CM1 · Vocabulaire · sens propre/figuré',
      preuve_url: null,
      instruction_audio: { cm1: 'absent' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CM1-VOC-03',
    schoolLevel: 'CM1',
    archetype: 'saisie',
    competence: 'FR-CM1-VOC-03',
    enonce: "Trouve un mot de la famille de 'voler' (avion). Réponse en un mot.",
    bonneReponse: 'vol',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO 17/04/2025 · cycle 3 · CM1 · Vocabulaire · dérivé',
      preuve_url: null,
      instruction_audio: { cm1: 'absent' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CM1-VOC-04',
    schoolLevel: 'CM1',
    archetype: 'qcm',
    competence: 'FR-CM1-VOC-04',
    enonce: "Quel registre de langue utilise 'tu bouffes quoi ?' ?",
    options: ['Soutenu', 'Courant', 'Familier', 'Technique'],
    bonneReponse: 'Familier',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO 17/04/2025 · cycle 3 · CM1 · Vocabulaire · niveaux de langue',
      preuve_url: null,
      instruction_audio: { cm1: 'absent' },
      stimulus_audio: { required: false },
    },
  },
];

const ITEMS_ECRITURE_ORTHOGRAPHE: CM1Item[] = [
  {
    id: 'FR-CM1-ORTH-01',
    schoolLevel: 'CM1',
    archetype: 'qcm',
    competence: 'FR-CM1-ORTH-01',
    enonce: "Quelle phrase est correcte ?",
    options: ['Les enfants chantent', 'Les enfants chantons', 'Les enfants chante', 'Les enfants chanter'],
    bonneReponse: 'Les enfants chantent',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO 17/04/2025 · cycle 3 · CM1 · Écriture · accord sujet-verbe',
      preuve_url: null,
      instruction_audio: { cm1: 'absent' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CM1-ORTH-02',
    schoolLevel: 'CM1',
    archetype: 'qcm',
    competence: 'FR-CM1-ORTH-02',
    enonce: "Quel homophone complète : 'Le médecin me ... une ordonnance.'",
    options: ['a', 'à', 'as', 'ah'],
    bonneReponse: 'a',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO 17/04/2025 · cycle 3 · CM1 · Écriture · homophones (a/à)',
      preuve_url: null,
      instruction_audio: { cm1: 'absent' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CM1-ORTH-03',
    schoolLevel: 'CM1',
    archetype: 'saisie',
    competence: 'FR-CM1-ORTH-03',
    enonce: 'Écris le pluriel de "un cheval" (plusieurs).',
    bonneReponse: 'des chevaux',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO 17/04/2025 · cycle 3 · CM1 · Écriture · pluriels irréguliers',
      preuve_url: null,
      instruction_audio: { cm1: 'absent' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CM1-ORTH-04',
    schoolLevel: 'CM1',
    archetype: 'production-ecrite',
    competence: 'FR-CM1-ORTH-04',
    enonce: 'Écris une phrase avec le verbe "manger" au présent avec le sujet "nous".',
    minWords: 1,
    evalMode: 'HUMAN',
    meta: {
      source_officielle: 'BO 17/04/2025 · cycle 3 · CM1 · Écriture · conjugaison',
      preuve_url: null,
      instruction_audio: { cm1: 'absent' },
      stimulus_audio: { required: false },
    },
  },
];

const ITEMS_CONSIGNES: CM1Item[] = [
  {
    id: 'FR-CM1-CONS-01',
    schoolLevel: 'CM1',
    archetype: 'qcm',
    competence: 'FR-CM1-CONS-01',
    enonce: "Consigne : 'Lis le texte, surligne les passages importants puis rédige un résumé en 5 lignes'. Combien d'actions ?",
    options: ['1', '2', '3', '4'],
    bonneReponse: '3',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO 17/04/2025 · cycle 3 · CM1 · Comprendre une consigne 3 temps',
      preuve_url: null,
      instruction_audio: { cm1: 'absent' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CM1-CONS-02',
    schoolLevel: 'CM1',
    archetype: 'qcm',
    competence: 'FR-CM1-CONS-02',
    enonce: "Consigne : 'Compare les deux tableaux et rédige une conclusion en t'aidant des données'. Que dois-tu faire ?",
    options: ['Comparer', 'Rédiger une conclusion', 'Comparer puis rédiger une conclusion', 'Effacer les données'],
    bonneReponse: 'Comparer puis rédiger une conclusion',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO 17/04/2025 · cycle 3 · CM1 · Consigne complexe multi-temporelle',
      preuve_url: null,
      instruction_audio: { cm1: 'absent' },
      stimulus_audio: { required: false },
    },
  },
];

const ITEMS_NUMERATION: CM1Item[] = [
  {
    id: 'MA-CM1-NB-01',
    schoolLevel: 'CM1',
    archetype: 'qcm',
    competence: 'MA-CM1-NB-01',
    enonce: 'Quel nombre est le plus grand ?',
    options: ['12,5', '12,7', '12,3', '12,9'],
    bonneReponse: '12,9',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO 17/04/2025 · cycle 3 · CM1 · Mathématiques · comparer décimaux',
      preuve_url: null,
      instruction_audio: { cm1: 'absent' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CM1-NB-02',
    schoolLevel: 'CM1',
    archetype: 'saisie',
    competence: 'MA-CM1-NB-02',
    enonce: 'Écris deux millions trois cent mille en chiffres.',
    bonneReponse: '2300000',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO 17/04/2025 · cycle 3 · CM1 · Mathématiques · lire/écrire grands nombres',
      preuve_url: null,
      instruction_audio: { cm1: 'absent' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CM1-NB-03',
    schoolLevel: 'CM1',
    archetype: 'qcm',
    competence: 'MA-CM1-NB-03',
    enonce: "Combien de dixièmes dans 0,3 ?",
    options: ['3', '30', '0,3', '300'],
    bonneReponse: '3',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO 17/04/2025 · cycle 3 · CM1 · Mathématiques · décimaux · dixièmes',
      preuve_url: null,
      instruction_audio: { cm1: 'absent' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CM1-NB-04',
    schoolLevel: 'CM1',
    archetype: 'qcm',
    competence: 'MA-CM1-NB-04',
    enonce: 'Range : 0,45 / 0,5 / 0,4',
    options: ['0,4 < 0,45 < 0,5', '0,5 < 0,4 < 0,45', '0,45 < 0,4 < 0,5', '0,4 < 0,5 < 0,45'],
    bonneReponse: '0,4 < 0,45 < 0,5',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO 17/04/2025 · cycle 3 · CM1 · Mathématiques · ranger décimaux',
      preuve_url: null,
      instruction_audio: { cm1: 'absent' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CM1-NB-05',
    schoolLevel: 'CM1',
    archetype: 'saisie',
    competence: 'MA-CM1-NB-05',
    enonce: "Complète : 4,25 = 4 + ___ .",
    bonneReponse: '0,25',
    tolerance: 1,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO 17/04/2025 · cycle 3 · CM1 · Mathématiques · décomposition décimaux',
      preuve_url: null,
      instruction_audio: { cm1: 'absent' },
      stimulus_audio: { required: false },
    },
  },
];

const ITEMS_CALCUL_MENTAL: CM1Item[] = [
  {
    id: 'MA-CM1-CAL-01',
    schoolLevel: 'CM1',
    archetype: 'saisie',
    competence: 'MA-CM1-CAL-01',
    enonce: 'Combien font 15 × 6 ?',
    bonneReponse: '90',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO 17/04/2025 · cycle 3 · CM1 · Mathématiques · tables multiplication étendues',
      preuve_url: null,
      instruction_audio: { cm1: 'absent' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CM1-CAL-02',
    schoolLevel: 'CM1',
    archetype: 'saisie',
    competence: 'MA-CM1-CAL-02',
    enonce: 'Combien font 35 × 4 ? (calcul mental)',
    bonneReponse: '140',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO 17/04/2025 · cycle 3 · CM1 · Mathématiques · multiplication mentale',
      preuve_url: null,
      instruction_audio: { cm1: 'absent' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CM1-CAL-03',
    schoolLevel: 'CM1',
    archetype: 'vf',
    competence: 'MA-CM1-CAL-03',
    enonce: '125 × 8 = 1000. Vrai ou faux ?',
    bonneReponse: true,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO 17/04/2025 · cycle 3 · CM1 · Mathématiques · multiplication grands nombres',
      preuve_url: null,
      instruction_audio: { cm1: 'absent' },
      stimulus_audio: { required: false },
    },
  },
];

const ITEMS_OPERATIONS: CM1Item[] = [
  {
    id: 'MA-CM1-OP-01',
    schoolLevel: 'CM1',
    archetype: 'saisie',
    competence: 'MA-CM1-OP-01',
    enonce: "Pose l'opération 234 × 56. Quel est le résultat ?",
    bonneReponse: '13104',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO 17/04/2025 · cycle 3 · CM1 · Mathématiques · multiplication posée plusieurs chiffres',
      preuve_url: null,
      instruction_audio: { cm1: 'absent' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CM1-OP-02',
    schoolLevel: 'CM1',
    archetype: 'saisie',
    competence: 'MA-CM1-OP-02',
    enonce: "Pose l'opération 4 256 ÷ 8. Quel est le quotient ?",
    bonneReponse: '532',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO 17/04/2025 · cycle 3 · CM1 · Mathématiques · division posée',
      preuve_url: null,
      instruction_audio: { cm1: 'absent' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CM1-OP-03',
    schoolLevel: 'CM1',
    archetype: 'manipulation-glisser',
    competence: 'MA-CM1-OP-03',
    enonce: "Pose l'opération 678 × 25. Quel est le résultat ?",
    spec: { operation: '678*25', format: 'colonnes' },
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO 17/04/2025 · cycle 3 · CM1 · Mathématiques · multiplication posée',
      preuve_url: null,
      instruction_audio: { cm1: 'absent' },
      stimulus_audio: { required: false },
    },
  },
];

const ITEMS_PROBLEMES: CM1Item[] = [
  {
    id: 'MA-CM1-PROB-01',
    schoolLevel: 'CM1',
    archetype: 'texte-a-trou',
    competence: 'MA-CM1-PROB-01',
    enonce: "Un libraire commande 15 livres à 12 € chacun et 8 cahiers à 4 € chacun. Quel est le montant total de sa commande ?",
    enonceSegments: ['Un libraire commande 15 livres à 12 € chacun et 8 cahiers à 4 € chacun. Quel est le montant total de sa commande ? Réponse : ', '{T1}'],
    trous: [{ id: 'T1', bonneReponse: '212', tolerance: 0 }],
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO 17/04/2025 · cycle 3 · CM1 · Mathématiques · Résolution · multi-étapes',
      preuve_url: null,
      instruction_audio: { cm1: 'absent' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CM1-PROB-02',
    schoolLevel: 'CM1',
    archetype: 'saisie',
    competence: 'MA-CM1-PROB-02',
    enonce: "Une classe de 28 élèves part en sortie. Le bus transporte 14 personnes à la fois. Combien de voyages ?",
    bonneReponse: '2',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO 17/04/2025 · cycle 3 · CM1 · Mathématiques · Résolution · division',
      preuve_url: null,
      instruction_audio: { cm1: 'absent' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CM1-PROB-03',
    schoolLevel: 'CM1',
    archetype: 'qcm',
    competence: 'MA-CM1-PROB-03',
    enonce: "Un jardin a une forme rectangulaire de 24 m par 18 m. Quelle est son aire en m² ?",
    options: ['42', '84', '432', '216'],
    bonneReponse: '432',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO 17/04/2025 · cycle 3 · CM1 · Mathématiques · aire rectangle',
      preuve_url: null,
      instruction_audio: { cm1: 'absent' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CM1-PROB-04',
    schoolLevel: 'CM1',
    archetype: 'saisie',
    competence: 'MA-CM1-PROB-04',
    enonce: "Un sportif parcourt 12 km le lundi, 15 km le mardi et 9 km le mercredi. Combien de km en moyenne par jour ?",
    bonneReponse: '12',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO 17/04/2025 · cycle 3 · CM1 · Mathématiques · moyenne',
      preuve_url: null,
      instruction_audio: { cm1: 'absent' },
      stimulus_audio: { required: false },
    },
  },
];

export const BANQUE_CM1: CM1Item[] = [
  ...ITEMS_LECTURE_CODE,
  ...ITEMS_FLUENCE,
  ...ITEMS_COMPREHENSION,
  ...ITEMS_VOCABULAIRE,
  ...ITEMS_ECRITURE_ORTHOGRAPHE,
  ...ITEMS_CONSIGNES,
  ...ITEMS_NUMERATION,
  ...ITEMS_CALCUL_MENTAL,
  ...ITEMS_OPERATIONS,
  ...ITEMS_PROBLEMES,
];

export const BANQUE_CM1_COUNT = BANQUE_CM1.length;
export const BANQUE_CM1_REPARTITION = {
  lecture_code: ITEMS_LECTURE_CODE.length,
  fluence: ITEMS_FLUENCE.length,
  comprehension: ITEMS_COMPREHENSION.length,
  vocabulaire: ITEMS_VOCABULAIRE.length,
  ecriture_orthographe: ITEMS_ECRITURE_ORTHOGRAPHE.length,
  consignes: ITEMS_CONSIGNES.length,
  numeration: ITEMS_NUMERATION.length,
  calcul_mental: ITEMS_CALCUL_MENTAL.length,
  operations: ITEMS_OPERATIONS.length,
  problemes: ITEMS_PROBLEMES.length,
};
